/**
 * CRC-32 (IEEE 802.3 — the zlib/PNG/S3 `x-amz-checksum-crc32` polynomial).
 *
 * Every scan is hashed before upload so the server can tell whether S3 already
 * holds exactly this file, and so S3 itself rejects a PUT whose bytes arrive
 * corrupted. Masters run to ~230 MB, so this uses slicing-by-8 (eight bytes per
 * step through eight lookup tables) rather than the byte-at-a-time loop, and
 * reads the file in chunks so memory stays flat however large it is.
 */

const POLY = 0xedb88320;

/** Eight 256-entry tables laid end to end: table k starts at k * 256. */
const TABLES: Uint32Array = (() => {
  const t = new Uint32Array(256 * 8);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? POLY ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  for (let n = 0; n < 256; n++) {
    for (let k = 1; k < 8; k++) {
      const prev = t[(k - 1) * 256 + n];
      t[k * 256 + n] = (prev >>> 8) ^ t[prev & 0xff];
    }
  }
  return t;
})();

/**
 * Fold `data` into a running CRC. Start from 0; the value returned after each
 * call is the finished CRC of everything fed so far, so chunks compose.
 */
export function crc32Update(crc: number, data: Uint8Array): number {
  const t = TABLES;
  let c = ~crc >>> 0;
  let i = 0;
  const n = data.length;
  while (n - i >= 8) {
    const a = c ^ (data[i] | (data[i + 1] << 8) | (data[i + 2] << 16) | (data[i + 3] << 24));
    c =
      t[1792 + (a & 0xff)] ^
      t[1536 + ((a >>> 8) & 0xff)] ^
      t[1280 + ((a >>> 16) & 0xff)] ^
      t[1024 + (a >>> 24)] ^
      t[768 + data[i + 4]] ^
      t[512 + data[i + 5]] ^
      t[256 + data[i + 6]] ^
      t[data[i + 7]];
    i += 8;
  }
  while (i < n) c = t[(c ^ data[i++]) & 0xff] ^ (c >>> 8);
  return ~c >>> 0;
}

/** S3's encoding of a CRC32: base64 of its four bytes, big-endian. */
export function crc32ToBase64(crc: number): string {
  return btoa(
    String.fromCharCode((crc >>> 24) & 0xff, (crc >>> 16) & 0xff, (crc >>> 8) & 0xff, crc & 0xff)
  );
}

/** 8 MB: big enough that per-chunk overhead vanishes, small enough to be cheap. */
const CHUNK_BYTES = 8 * 1024 * 1024;

function readChunk(blob: Blob): Promise<ArrayBuffer> {
  if (typeof blob.arrayBuffer === 'function') return blob.arrayBuffer();
  // Older engines (and jsdom) without Blob#arrayBuffer.
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error ?? new Error('Could not read file'));
    reader.readAsArrayBuffer(blob);
  });
}

/** CRC32 of a whole blob, in S3's base64 form. */
export async function hashBlobCrc32(blob: Blob): Promise<string> {
  let crc = 0;
  for (let offset = 0; offset < blob.size; offset += CHUNK_BYTES) {
    const chunk = await readChunk(blob.slice(offset, offset + CHUNK_BYTES));
    crc = crc32Update(crc, new Uint8Array(chunk));
  }
  return crc32ToBase64(crc);
}
